import { useState, useEffect } from "react";

type Props = {
  active: string;
  onSelect: (key: string) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  isStaff?: boolean;
  isPractitioner?: boolean;
};

const NAV = [
  { key: "home",    icon: "⌂",  label: "Dashboard" },
  { key: "body",    icon: "✦",  label: "LifeScribe" },
  { key: "history", icon: "📊", label: "History" },
  { key: "cal",     icon: "▣",  label: "Schedule" },
  { key: "coach",   icon: "◎",  label: "Coach" },
  { key: "labs",    icon: "◐",  label: "Labs" },
  { key: "devices", icon: "⌚",  label: "Devices" },
];

// Role-gated entries, appended below a divider.
const STAFF_NAV = [
  { key: "ops", icon: "❖", label: "Ops" },
];
const PRACTITIONER_NAV = [
  { key: "practitioner", icon: "✚", label: "Clients" },
];

export function LeftNav({ active, onSelect, collapsed, onToggleCollapse, isStaff, isPractitioner }: Props) {
  const items = [
    ...NAV,
    ...(isPractitioner ? PRACTITIONER_NAV : []),
    ...(isStaff ? STAFF_NAV : []),
  ];
  return (
    <nav
      className={`border-r border-d_border/20 flex flex-col pt-2 bg-d_sf transition-[width] duration-200 ${
        collapsed ? "items-center" : "items-stretch px-2"
      }`}
    >
      {/* Collapse toggle */}
      <button
        onClick={onToggleCollapse}
        title={collapsed ? "Expand menu" : "Collapse menu"}
        aria-label={collapsed ? "Expand menu" : "Collapse menu"}
        className={`mb-1.5 h-8 rounded-lg flex items-center text-d_text3 hover:text-d_text2 hover:bg-d_accent/5 transition-colors ${
          collapsed ? "w-8 justify-center" : "w-full justify-end px-2"
        }`}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {collapsed
            ? <><line x1="3" y1="12" x2="21" y2="12" /><polyline points="13 6 19 12 13 18" /></>
            : <><line x1="21" y1="12" x2="3" y2="12" /><polyline points="11 6 5 12 11 18" /></>}
        </svg>
      </button>

      {items.map((n) => {
        const isActive = active === n.key;
        const roleKeys = [...PRACTITIONER_NAV, ...STAFF_NAV].map((s) => s.key);
        // Divider above the FIRST role-gated entry only.
        const firstRoleKey = roleKeys.find((k) => items.some((it) => it.key === k));
        const isStaffItem = n.key === firstRoleKey;
        return (
          <button
            key={n.key}
            onClick={() => onSelect(n.key)}
            title={collapsed ? n.label : undefined}
            className={`h-9 rounded-lg flex items-center transition-colors mb-0.5 ${
              collapsed ? "w-8 justify-center" : "w-full px-2.5 gap-2.5"
            } ${
              isActive
                ? "text-d_accent bg-d_accent/10"
                : "text-d_text3 hover:text-d_text2 hover:bg-d_accent/5"
            } ${isStaffItem ? "mt-1.5 border-t border-d_border/15 pt-1.5 h-[42px]" : ""}`}
          >
            <span className="text-sm flex-shrink-0 w-4 text-center">{n.icon}</span>
            {!collapsed && (
              <span className={`text-[12px] font-medium tracking-[0.01em] whitespace-nowrap ${isActive ? "" : ""}`}>
                {n.label}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

/** Persisted collapse state hook. Defaults to expanded on wide screens, collapsed on narrow. */
export function useNavCollapsed() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem("yvree-nav-collapsed");
      if (stored !== null) return stored === "true";
    } catch { /* noop */ }
    // Default: collapsed under 1100px, expanded above
    return typeof window !== "undefined" ? window.innerWidth < 1100 : false;
  });

  useEffect(() => {
    try { localStorage.setItem("yvree-nav-collapsed", String(collapsed)); } catch { /* noop */ }
  }, [collapsed]);

  return { collapsed, toggle: () => setCollapsed((c) => !c), setCollapsed };
}
